import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
    FileText, 
    Upload, 
    Check, 
    X, 
    Download, 
    Link, 
    AlertTriangle,
    Filter,
    RefreshCw,
    Eye,
    Pen,
    Loader2,
    FileCheck,
    Search,
    CheckCircle,
    XCircle,
    Mail,
    Send,
    ExternalLink,
    Clock,
    CheckCheck,
    Edit3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
    pending: { label: 'Pending', color: 'bg-amber-500', textColor: 'text-amber-400' },
    approved: { label: 'Approved', color: 'bg-green-500', textColor: 'text-green-400' },
    rejected: { label: 'Rejected', color: 'bg-red-500', textColor: 'text-red-400' },
    uploaded: { label: 'Uploaded', color: 'bg-blue-500', textColor: 'text-blue-400' }
};

const TYPE_CONFIG = {
    ratecon: { label: 'Rate Con', color: 'text-blue-400' },
    bol: { label: 'BOL', color: 'text-purple-400' },
    pod: { label: 'POD', color: 'text-green-400' },
    other: { label: 'Other', color: 'text-zinc-400' }
};

export default function RateConsPage() {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState('all');
    const [matchedFilter, setMatchedFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Upload modal
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadLoadId, setUploadLoadId] = useState('');
    const [uploadDocType, setUploadDocType] = useState('ratecon');
    const [uploadBroker, setUploadBroker] = useState('');
    
    // Attach modal
    const [showAttachModal, setShowAttachModal] = useState(false);
    const [attachDocId, setAttachDocId] = useState(null);
    const [attachLoadId, setAttachLoadId] = useState('');
    
    // Sign modal
    const [showSignModal, setShowSignModal] = useState(false);
    const [signDocId, setSignDocId] = useState(null);
    const [signDoc, setSignDoc] = useState(null);
    const [acknowledgeDiscrepancies, setAcknowledgeDiscrepancies] = useState(false);
    
    // Reject modal
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectDocId, setRejectDocId] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    
    // Email modal
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailDoc, setEmailDoc] = useState(null);
    const [emailData, setEmailData] = useState(null);
    const [emailMode, setEmailMode] = useState('draft'); // 'draft' or 'api'
    const [emailLoading, setEmailLoading] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [hasEmailIntegration, setHasEmailIntegration] = useState(false);

    // Check email integration status on mount
    useEffect(() => {
        const checkEmailIntegration = async () => {
            try {
                const res = await fetch(`${API_URL}/api/integrations?category=email`, { credentials: 'include' });
                if (res.ok) {
                    const integrations = await res.json();
                    const enabledEmail = integrations.find(i => i.is_enabled);
                    if (enabledEmail) {
                        setHasEmailIntegration(true);
                        const mode = enabledEmail.additional_config?.mode || 'draft';
                        setEmailMode(mode);
                    } else {
                        setHasEmailIntegration(false);
                        setEmailMode('draft');
                    }
                }
            } catch (error) {
                console.error('Failed to check email integration:', error);
            }
        };
        checkEmailIntegration();
    }, []);

    const fetchDocuments = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (statusFilter !== 'all') params.append('status', statusFilter);
            if (typeFilter !== 'all') params.append('document_type', typeFilter);
            if (matchedFilter === 'matched') params.append('matched', 'true');
            if (matchedFilter === 'unmatched') params.append('matched', 'false');
            
            const [docsRes, statsRes] = await Promise.all([
                fetch(`${API_URL}/api/load-documents?${params}`, { credentials: 'include' }),
                fetch(`${API_URL}/api/load-documents/stats`, { credentials: 'include' })
            ]);

            if (docsRes.ok) {
                setDocuments(await docsRes.json());
            }
            if (statsRes.ok) {
                setStats(await statsRes.json());
            }
        } catch (error) {
            console.error('Failed to fetch documents:', error);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, matchedFilter, typeFilter]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleUpload = async () => {
        if (!uploadFile) {
            toast.error('Please select a file');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('document_type', uploadDocType);
        if (uploadLoadId) formData.append('load_id', uploadLoadId);
        if (uploadBroker) formData.append('broker_name', uploadBroker);

        try {
            const response = await fetch(`${API_URL}/api/documents/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                toast.success(data.message);
                setShowUploadModal(false);
                setUploadFile(null);
                setUploadLoadId('');
                setUploadBroker('');
                fetchDocuments();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleAttach = async () => {
        if (!attachLoadId) {
            toast.error('Please enter a Load ID');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/load-documents/${attachDocId}/attach?load_id=${attachLoadId}`, {
                method: 'PATCH',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Document attached to load');
                setShowAttachModal(false);
                setAttachDocId(null);
                setAttachLoadId('');
                fetchDocuments();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to attach');
            }
        } catch (error) {
            toast.error('Failed to attach document');
        }
    };

    const handleSign = async () => {
        try {
            const response = await fetch(`${API_URL}/api/load-documents/${signDocId}/sign?acknowledge_discrepancies=${acknowledgeDiscrepancies}`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Document signed successfully');
                setShowSignModal(false);
                setSignDocId(null);
                setSignDoc(null);
                setAcknowledgeDiscrepancies(false);
                fetchDocuments();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to sign');
            }
        } catch (error) {
            toast.error('Failed to sign document');
        }
    };

    const handleReject = async () => {
        if (!rejectReason) {
            toast.error('Please provide a rejection reason');
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/load-documents/${rejectDocId}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ reason: rejectReason })
            });

            if (response.ok) {
                toast.success('Document rejected');
                setShowRejectModal(false);
                setRejectDocId(null);
                setRejectReason('');
                fetchDocuments();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to reject');
            }
        } catch (error) {
            toast.error('Failed to reject document');
        }
    };

    const handleCheckDiscrepancies = async (docId) => {
        try {
            const response = await fetch(`${API_URL}/api/load-documents/${docId}/check-discrepancies`, {
                method: 'POST',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.discrepancies_found) {
                    toast.warning(`Discrepancies found: ${data.details.length} issues`);
                } else {
                    toast.success('No discrepancies found');
                }
                fetchDocuments();
            } else {
                const error = await response.json();
                toast.error(error.detail?.message || error.detail || 'Failed to check');
            }
        } catch (error) {
            toast.error('Failed to check discrepancies');
        }
    };

    const handleDownload = async (docId, signed = false) => {
        window.open(`${API_URL}/api/load-documents/${docId}/download?signed=${signed}`, '_blank');
    };

    const openSignModal = (doc) => {
        setSignDocId(doc.document_id);
        setSignDoc(doc);
        setAcknowledgeDiscrepancies(false);
        setShowSignModal(true);
    };

    // Email handlers
    const openEmailModal = async (doc) => {
        setEmailDoc(doc);
        setEmailData(null);
        setShowEmailModal(true);
        setEmailLoading(true);
        
        try {
            // Prepare email to get content
            const response = await fetch(`${API_URL}/api/ratecon/${doc.document_id}/prepare-email`, {
                method: 'POST',
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                setEmailData(data);
                fetchDocuments(); // Refresh to show updated status
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to prepare email');
                setShowEmailModal(false);
            }
        } catch (error) {
            toast.error('Failed to prepare email');
            setShowEmailModal(false);
        } finally {
            setEmailLoading(false);
        }
    };

    const handleSendEmail = async () => {
        if (!emailDoc || !emailData) return;
        
        setEmailSending(true);
        try {
            const response = await fetch(`${API_URL}/api/ratecon/${emailDoc.document_id}/send-email`, {
                method: 'POST',
                credentials: 'include'
            });
            
            if (response.ok) {
                const data = await response.json();
                toast.success(data.message || 'Email sent successfully!');
                setShowEmailModal(false);
                setEmailDoc(null);
                setEmailData(null);
                fetchDocuments();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to send email');
            }
        } catch (error) {
            toast.error('Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };

    const handleOpenMailto = () => {
        if (emailData?.mailto_link) {
            window.open(emailData.mailto_link, '_blank');
            toast.success('Email draft opened in your mail client');
            setShowEmailModal(false);
        }
    };

    const getEmailStatusBadge = (doc) => {
        const status = doc.ratecon_email_status || 'not_sent';
        switch (status) {
            case 'sent':
                return (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                        <CheckCheck className="w-3 h-3 mr-1" />
                        Email Sent
                    </Badge>
                );
            case 'prepared':
                return (
                    <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        Email Prepared
                    </Badge>
                );
            default:
                return null;
        }
    };

    // Filter documents by search term
    const filteredDocuments = documents.filter(doc => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            doc.original_filename?.toLowerCase().includes(term) ||
            doc.broker_name?.toLowerCase().includes(term) ||
            doc.load_id?.toLowerCase().includes(term) ||
            doc.document_id?.toLowerCase().includes(term)
        );
    });

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="ratecons-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase flex items-center gap-2">
                        <FileText className="w-6 h-6 text-orange-500" />
                        Rate Confirmations
                    </h1>
                    <p className="text-zinc-500 text-sm mt-1">Manage uploaded documents and rate confirmations</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchDocuments()}
                        className="border-zinc-700 text-zinc-400"
                        data-testid="refresh-docs-btn"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => setShowUploadModal(true)}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                        data-testid="upload-doc-btn"
                    >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Document
                    </Button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="bg-zinc-900/50 border-zinc-800">
                        <CardContent className="p-4">
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Total</p>
                            <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Matched</p>
                            <p className="text-2xl font-bold text-green-400 mt-1">{stats.matched}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-amber-500">
                        <CardContent className="p-4">
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Unmatched</p>
                            <p className="text-2xl font-bold text-amber-400 mt-1">{stats.unmatched}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Signed</p>
                            <p className="text-2xl font-bold text-blue-400 mt-1">{stats.signed}</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-red-500">
                        <CardContent className="p-4">
                            <p className="text-zinc-500 text-xs uppercase tracking-wider">Discrepancies</p>
                            <p className="text-2xl font-bold text-red-400 mt-1">{stats.with_discrepancies}</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Filters */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-zinc-500" />
                            <span className="text-zinc-400 text-sm">Filters:</span>
                        </div>
                        <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                            <Input
                                placeholder="Search documents..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-zinc-800 border-zinc-700 text-white pl-10 w-48"
                                data-testid="search-docs"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="status-filter">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-700">
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="approved">Approved</SelectItem>
                                <SelectItem value="rejected">Rejected</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={matchedFilter} onValueChange={setMatchedFilter}>
                            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="matched-filter">
                                <SelectValue placeholder="Match" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-700">
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="matched">Matched</SelectItem>
                                <SelectItem value="unmatched">Unmatched</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={typeFilter} onValueChange={setTypeFilter}>
                            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700" data-testid="type-filter">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-700">
                                <SelectItem value="all">All Types</SelectItem>
                                <SelectItem value="ratecon">Rate Con</SelectItem>
                                <SelectItem value="bol">BOL</SelectItem>
                                <SelectItem value="pod">POD</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                        <span className="text-zinc-500 text-sm ml-auto">
                            {filteredDocuments.length} documents
                        </span>
                    </div>
                </CardContent>
            </Card>

            {/* Documents List */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                </div>
            ) : filteredDocuments.length === 0 ? (
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-12 text-center">
                        <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-white mb-2">No Documents</h3>
                        <p className="text-zinc-500 mb-4">
                            {documents.length === 0 
                                ? "Upload rate confirmations or forward them via email."
                                : "No documents match your filters."}
                        </p>
                        {documents.length === 0 && (
                            <Button onClick={() => setShowUploadModal(true)} className="bg-orange-500 hover:bg-orange-600">
                                <Upload className="w-4 h-4 mr-2" />
                                Upload First Document
                            </Button>
                        )}
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filteredDocuments.map((doc) => {
                        const statusConfig = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                        const typeConfig = TYPE_CONFIG[doc.document_type] || TYPE_CONFIG.other;

                        return (
                            <Card 
                                key={doc.document_id}
                                className={`bg-zinc-900/50 border-zinc-800 hover:bg-zinc-800/50 transition-colors ${doc.discrepancy_flag ? 'border-l-4 border-l-red-500' : ''}`}
                                data-testid={`doc-card-${doc.document_id}`}
                            >
                                <CardContent className="p-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4 flex-1">
                                            <div className="p-2 bg-zinc-800 rounded-lg">
                                                <FileText className={`w-6 h-6 ${typeConfig.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <span className="text-white font-medium truncate">
                                                        {doc.original_filename}
                                                    </span>
                                                    <Badge className={`${statusConfig.color} text-white text-xs`}>
                                                        {statusConfig.label}
                                                    </Badge>
                                                    <Badge variant="outline" className={`${typeConfig.color} border-current text-xs`}>
                                                        {typeConfig.label}
                                                    </Badge>
                                                    {doc.signed && (
                                                        <Badge className="bg-blue-500 text-white text-xs">
                                                            <CheckCircle className="w-3 h-3 mr-1" />
                                                            Signed
                                                        </Badge>
                                                    )}
                                                    {doc.discrepancy_flag && (
                                                        <Badge className="bg-red-500 text-white text-xs">
                                                            <AlertTriangle className="w-3 h-3 mr-1" />
                                                            Discrepancy
                                                        </Badge>
                                                    )}
                                                    {doc.ai_checked && !doc.discrepancy_flag && (
                                                        <Badge className="bg-green-500/20 text-green-400 text-xs">
                                                            <CheckCircle className="w-3 h-3 mr-1" />
                                                            AI Verified
                                                        </Badge>
                                                    )}
                                                    {getEmailStatusBadge(doc)}
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-zinc-400">
                                                    {doc.broker_name && (
                                                        <span>Broker: {doc.broker_name}</span>
                                                    )}
                                                    {doc.load_id ? (
                                                        <span 
                                                            className="text-green-400 cursor-pointer hover:text-green-300 hover:underline"
                                                            onClick={() => navigate(`/office-desk/${doc.load_id}`)}
                                                            title="Open in Office Desk"
                                                        >
                                                            <Link className="w-3 h-3 inline mr-1" />
                                                            {doc.load_id}
                                                        </span>
                                                    ) : (
                                                        <span className="text-amber-400">No load attached</span>
                                                    )}
                                                    <span>Via: {doc.uploaded_via}</span>
                                                    <span>{formatDate(doc.created_at)}</span>
                                                </div>
                                                {doc.discrepancy_details && doc.discrepancy_details.length > 0 && (
                                                    <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                                                        <p className="text-red-400 text-xs font-semibold mb-1">Discrepancies Found:</p>
                                                        <ul className="text-red-300 text-xs space-y-1">
                                                            {doc.discrepancy_details.map((d, i) => (
                                                                <li key={i}>• {d}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {/* Edit Load in Office Desk - only for docs with attached load */}
                                            {doc.load_id && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => navigate(`/office-desk/${doc.load_id}`)}
                                                    className="text-blue-400 hover:text-blue-300"
                                                    title="Edit Load in Office Desk"
                                                    data-testid={`edit-load-${doc.document_id}`}
                                                >
                                                    <Edit3 className="w-4 h-4" />
                                                </Button>
                                            )}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDownload(doc.document_id)}
                                                className="text-zinc-400 hover:text-white"
                                                title="Download"
                                            >
                                                <Download className="w-4 h-4" />
                                            </Button>
                                            {!doc.ai_checked && doc.document_type === 'ratecon' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleCheckDiscrepancies(doc.document_id)}
                                                    className="text-zinc-400 hover:text-white"
                                                    title="AI Check"
                                                >
                                                    <FileCheck className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {!doc.load_id && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setAttachDocId(doc.document_id);
                                                        setShowAttachModal(true);
                                                    }}
                                                    className="text-amber-400 hover:text-amber-300"
                                                    title="Attach to Load"
                                                >
                                                    <Link className="w-4 h-4" />
                                                </Button>
                                            )}
                                            {doc.status === 'pending' && !doc.signed && !doc.load_id && (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openSignModal(doc)}
                                                        className="text-green-400 hover:text-green-300"
                                                        title="Approve & Sign"
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setRejectDocId(doc.document_id);
                                                            setShowRejectModal(true);
                                                        }}
                                                        className="text-red-400 hover:text-red-300"
                                                        title="Reject"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </>
                                            )}
                                            {/* Send Email Button - only for signed docs with load attached */}
                                            {doc.signed && doc.load_id && doc.ratecon_email_status !== 'sent' && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => openEmailModal(doc)}
                                                    className="text-orange-400 hover:text-orange-300"
                                                    title={emailMode === 'api' && hasEmailIntegration ? 'Send to Broker' : 'Prepare Email'}
                                                    data-testid={`send-email-btn-${doc.document_id}`}
                                                >
                                                    <Mail className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Upload Modal */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Upload Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Upload a rate confirmation, BOL, POD, or other document.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">File (PDF only)</Label>
                            <Input
                                type="file"
                                accept=".pdf"
                                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Document Type</Label>
                            <Select value={uploadDocType} onValueChange={setUploadDocType}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-900 border-zinc-700">
                                    <SelectItem value="ratecon">Rate Confirmation</SelectItem>
                                    <SelectItem value="bol">Bill of Lading (BOL)</SelectItem>
                                    <SelectItem value="pod">Proof of Delivery (POD)</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-white">Load ID (optional)</Label>
                            <Input
                                value={uploadLoadId}
                                onChange={(e) => setUploadLoadId(e.target.value)}
                                placeholder="e.g., LD-ABC123"
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Broker Name (optional)</Label>
                            <Input
                                value={uploadBroker}
                                onChange={(e) => setUploadBroker(e.target.value)}
                                placeholder="e.g., XPO Logistics"
                                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowUploadModal(false)} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button onClick={handleUpload} disabled={uploading || !uploadFile} className="bg-orange-500 hover:bg-orange-600">
                            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                            Upload
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Attach to Load Modal */}
            <Dialog open={showAttachModal} onOpenChange={setShowAttachModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Attach to Load</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Link this document to a load.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="text-white">Load ID</Label>
                        <Input
                            value={attachLoadId}
                            onChange={(e) => setAttachLoadId(e.target.value)}
                            placeholder="e.g., LD-ABC123"
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAttachModal(false)} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button onClick={handleAttach} disabled={!attachLoadId} className="bg-orange-500 hover:bg-orange-600">
                            <Link className="w-4 h-4 mr-2" />
                            Attach
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Sign Modal */}
            <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Approve & Sign Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Sign this rate confirmation to approve it.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4 space-y-4">
                        {signDoc?.discrepancy_flag && (
                            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                                    <div>
                                        <p className="text-red-400 font-semibold">Discrepancies Detected</p>
                                        <p className="text-red-300 text-sm mt-1">
                                            This document has flagged discrepancies. Review carefully before signing.
                                        </p>
                                        {signDoc.discrepancy_details && (
                                            <ul className="text-red-300 text-sm mt-2 space-y-1">
                                                {signDoc.discrepancy_details.map((d, i) => (
                                                    <li key={i}>• {d}</li>
                                                ))}
                                            </ul>
                                        )}
                                        <label className="flex items-center gap-2 mt-4 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={acknowledgeDiscrepancies}
                                                onChange={(e) => setAcknowledgeDiscrepancies(e.target.checked)}
                                                className="rounded"
                                            />
                                            <span className="text-white text-sm">
                                                I acknowledge the discrepancies and approve signing
                                            </span>
                                        </label>
                                    </div>
                                </div>
                            </div>
                        )}
                        <p className="text-zinc-400 text-sm">
                            By signing, you approve this document. The broker will NOT be automatically notified - you must send the signed document manually.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowSignModal(false)} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleSign} 
                            disabled={signDoc?.discrepancy_flag && !acknowledgeDiscrepancies}
                            className="bg-green-500 hover:bg-green-600"
                        >
                            <Pen className="w-4 h-4 mr-2" />
                            Sign Document
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Modal */}
            <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white">Reject Document</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Provide a reason for rejecting this document.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Label className="text-white">Rejection Reason</Label>
                        <Input
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="e.g., Incorrect rate, wrong load details"
                            className="bg-zinc-800 border-zinc-700 text-white mt-1"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRejectModal(false)} className="border-zinc-700">
                            Cancel
                        </Button>
                        <Button onClick={handleReject} disabled={!rejectReason} className="bg-red-500 hover:bg-red-600">
                            <X className="w-4 h-4 mr-2" />
                            Reject
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Email Modal */}
            <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Mail className="w-5 h-5 text-orange-500" />
                            {emailMode === 'api' && hasEmailIntegration ? 'Send to Broker' : 'Email Rate Confirmation'}
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {emailMode === 'api' && hasEmailIntegration 
                                ? 'Send the signed rate confirmation directly to the broker.'
                                : 'Prepare an email draft with the signed rate confirmation.'}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {emailLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                        </div>
                    ) : emailData ? (
                        <div className="space-y-4 py-4">
                            {/* Email Preview */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-zinc-500 w-16">To:</span>
                                    <span className="text-white">{emailData.to_email}</span>
                                </div>
                                {emailData.from_email && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <span className="text-zinc-500 w-16">From:</span>
                                        <span className="text-white">{emailData.from_email}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-zinc-500 w-16">Subject:</span>
                                    <span className="text-white">{emailData.subject}</span>
                                </div>
                            </div>
                            
                            {/* Email Body Preview */}
                            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                                <p className="text-zinc-300 text-sm whitespace-pre-line">{emailData.body}</p>
                            </div>
                            
                            {/* Attachment Info */}
                            <div className="flex items-center gap-2 text-sm text-zinc-400">
                                <FileText className="w-4 h-4" />
                                <span>Attachment: {emailData.attachment_filename}</span>
                            </div>
                            
                            {/* Mode Indicator */}
                            <div className={`p-3 rounded-lg border ${
                                emailMode === 'api' && hasEmailIntegration
                                    ? 'bg-green-500/10 border-green-500/30'
                                    : 'bg-amber-500/10 border-amber-500/30'
                            }`}>
                                {emailMode === 'api' && hasEmailIntegration ? (
                                    <div className="flex items-start gap-2">
                                        <Send className="w-4 h-4 text-green-400 mt-0.5" />
                                        <div>
                                            <p className="text-green-400 font-medium text-sm">Direct Send Mode</p>
                                            <p className="text-green-300 text-xs mt-1">
                                                Click "Send to Broker" to send this email directly via your configured email provider.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-start gap-2">
                                        <ExternalLink className="w-4 h-4 text-amber-400 mt-0.5" />
                                        <div>
                                            <p className="text-amber-400 font-medium text-sm">Draft Mode</p>
                                            <p className="text-amber-300 text-xs mt-1">
                                                This will open your email client with a pre-filled draft. You'll need to attach the signed PDF manually.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : null}
                    
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowEmailModal(false)} 
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        {emailData && (
                            emailMode === 'api' && hasEmailIntegration ? (
                                <Button 
                                    onClick={handleSendEmail} 
                                    disabled={emailSending}
                                    className="bg-orange-500 hover:bg-orange-600"
                                    data-testid="confirm-send-email-btn"
                                >
                                    {emailSending ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4 mr-2" />
                                    )}
                                    Send to Broker
                                </Button>
                            ) : (
                                <Button 
                                    onClick={handleOpenMailto}
                                    className="bg-orange-500 hover:bg-orange-600"
                                    data-testid="open-mailto-btn"
                                >
                                    <ExternalLink className="w-4 h-4 mr-2" />
                                    Open Email Draft
                                </Button>
                            )
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
